import { createContext, ReactNode, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router";

import useTypedContext from "hooks/useTypedContext";

import { SpacesContext } from "../SpacesProvider";
import { createNavigationLink, makeFilterUrlParams } from "./utils";
import { SpacesViewContext } from "./Context";
import { CREATE_NEW_URL_PARAM, PARENT_ID_URL_PARAM } from "./constants";
import { showDeleteConfirmation } from "./DeleteConfirmation";
import { SpacesDrawerWriteMode } from "./types";

type SpacesViewActionsContextProps = {
  onCreate: (parentId?: string) => void;
  onSelect: (id: string) => void;
  onCloseDrawer: (replace?: boolean) => void;
  onFilter: (id: string, path: string) => void;
  onDelete: (id: string) => void;
  registerBeforeSpaceNavigate: (fn: () => boolean | Promise<boolean>) => () => void;
};

export const SpacesViewActionsContext = createContext<SpacesViewActionsContextProps | undefined>(
  undefined
);
SpacesViewActionsContext.displayName = "SpacesViewActionsContext";

type SpacesViewActionsProviderProps = {
  children: ReactNode;
};

export const SpacesViewActionsProvider = ({ children }: SpacesViewActionsProviderProps) => {
  const { spaces, spaceHierarchy } = useTypedContext(SpacesContext);
  const navigate = useNavigate();
  const location = useLocation();
  const navigatingAwayViaSpaceFilterRef = useRef(false);
  const beforeSpaceNavigateRef = useRef<(() => Promise<boolean>) | null>(null);
  const { currentSpace, drawerWriteMode } = useTypedContext(SpacesViewContext);
  const { spaceId } = useParams<{ spaceId?: string }>();

  const isCreateDrawer = drawerWriteMode === SpacesDrawerWriteMode.Create;

  const handleCreateSpace = useCallback(
    (parentId?: string) => {
      const newUrlParams = new URLSearchParams();
      newUrlParams.set(CREATE_NEW_URL_PARAM, "true");

      if (parentId) {
        newUrlParams.set(PARENT_ID_URL_PARAM, parentId);
      }

      navigate(createNavigationLink(`/spaces`, newUrlParams));
    },
    [navigate]
  );

  const registerBeforeSpaceNavigate = useCallback((fn: () => boolean | Promise<boolean>) => {
    const wrapped = async () => Boolean(await fn());
    beforeSpaceNavigateRef.current = wrapped;
    return () => {
      beforeSpaceNavigateRef.current = null;
    };
  }, []);

  const handleSpaceSelect = useCallback(
    async (id: string) => {
      const guard = beforeSpaceNavigateRef.current;
      if (guard) {
        const ok = await guard();
        if (!ok) {
          return;
        }
      }

      navigate(`/spaces/${id}`);
    },
    [navigate]
  );

  const handleOnFilter = useCallback(
    (id: string, path: string) => {
      // `createDrawer` will hide the modal when the route leaves `/spaces`; that triggers
      // `onCloseDrawer` while the URL may still show `/spaces/:id`. Skip the `/spaces` redirect
      // so it does not race with `navigate` below (same issue as pathname guard).
      navigatingAwayViaSpaceFilterRef.current = true;
      const spacePath = spaceHierarchy[id];

      const urlParams = makeFilterUrlParams(spacePath);

      navigate(`${path}?${urlParams}`);
    },
    [navigate, spaceHierarchy]
  );

  const handleCloseDrawer = useCallback(
    (replace?: boolean) => {
      if (navigatingAwayViaSpaceFilterRef.current) {
        navigatingAwayViaSpaceFilterRef.current = false;
        return;
      }

      // Drawer close after navigating to stacks/blueprints/etc. must not send us back to Spaces.
      if (!location.pathname.startsWith("/spaces")) {
        return;
      }

      if (!spaceId && !isCreateDrawer) {
        // If no space is selected, drawer is already closed,
        // No need to navigate
        return;
      }

      navigate(createNavigationLink(`/spaces`), {
        replace,
      });
    },
    [isCreateDrawer, navigate, spaceId, location.pathname]
  );

  const handleOnDelete = useCallback(
    (id: string) => {
      const space = spaces.find((space) => space.id === id);

      if (!space) {
        return;
      }

      showDeleteConfirmation({ name: space.name, id: space.id }).then(() => {
        if (currentSpace?.id === space.id) {
          handleCloseDrawer(true);
        }
      });
    },
    [spaces, currentSpace?.id, handleCloseDrawer]
  );

  return (
    <SpacesViewActionsContext.Provider
      value={{
        onCreate: handleCreateSpace,
        onSelect: handleSpaceSelect,
        onCloseDrawer: handleCloseDrawer,
        onFilter: handleOnFilter,
        onDelete: handleOnDelete,
        registerBeforeSpaceNavigate,
      }}
    >
      {children}
    </SpacesViewActionsContext.Provider>
  );
};
